"use client";

/**
 * Reusable "Export" dropdown menu.
 *
 * Used everywhere we want a `[Export ▾]` button that opens to one or more
 * download / copy actions. Keep call sites short by using the
 * `csvExportItems` helper for the common "Download CSV + Copy to clipboard"
 * pair; pass arbitrary `items` for multi-format or multi-CSV menus.
 */

import { useState, type ReactNode } from "react";
import { ClipboardCopy, Download, type LucideIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { copyCsv, downloadCsv, downloadText } from "@/lib/export/csv";

export interface ExportMenuItem {
  /** Stable key for React. Defaults to label. */
  key?: string;
  label: string;
  icon?: LucideIcon;
  /** Triggered when the user picks this item. May be async. */
  onSelect: () => void | Promise<void>;
  disabled?: boolean;
  /** Inserts a `<DropdownMenuSeparator />` before this item. */
  separatorBefore?: boolean;
}

export interface ExportMenuProps {
  items: ExportMenuItem[];
  /** Trigger button copy. Defaults to "Export". */
  buttonLabel?: ReactNode;
  buttonSize?: "default" | "sm" | "lg" | "icon";
  buttonVariant?:
    | "default"
    | "outline"
    | "ghost"
    | "secondary"
    | "link"
    | "destructive";
  /** Hide the leading download icon in the trigger (useful for inline menus). */
  hideTriggerIcon?: boolean;
  /** Disable the whole trigger button. */
  disabled?: boolean;
  /** `align` prop forwarded to `<DropdownMenuContent />`. */
  align?: "start" | "center" | "end";
  /** Optional extra class on the trigger button. */
  className?: string;
}

export function ExportMenu({
  items,
  buttonLabel = "Export",
  buttonSize = "sm",
  buttonVariant = "outline",
  hideTriggerIcon = false,
  disabled = false,
  align = "end",
  className,
}: ExportMenuProps) {
  // Track whether any item is currently running so the trigger can show
  // a spinner. Critical for async build() calls that fetch from an API.
  const [busy, setBusy] = useState(false);

  if (items.length === 0) return null;

  const handleSelect = async (item: ExportMenuItem) => {
    if (item.disabled) return;
    setBusy(true);
    try {
      await item.onSelect();
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          disabled={disabled}
          loading={busy}
          className={className}
        >
          {!busy && !hideTriggerIcon && <Download className="h-4 w-4 mr-2" />}
          {buttonLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {items.map((item, idx) => {
          const Icon = item.icon;
          const node = (
            <DropdownMenuItem
              key={item.key ?? `${item.label}-${idx}`}
              disabled={item.disabled}
              onSelect={(event) => {
                // Keep menu interaction snappy; close immediately and run
                // the action in the background. Errors are surfaced by the
                // helper that built the item (e.g. csvExportItems).
                event.preventDefault();
                void handleSelect(item);
              }}
            >
              {Icon && <Icon className="h-4 w-4 mr-2" />}
              {item.label}
            </DropdownMenuItem>
          );

          if (item.separatorBefore && idx > 0) {
            return (
              <div key={`group-${item.key ?? idx}`}>
                <DropdownMenuSeparator />
                {node}
              </div>
            );
          }
          return node;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Common item builders ────────────────────────────────────────────────────

export interface CsvExportConfig {
  /** File name written to the user's disk on download. */
  filename: string;
  /** Returns the CSV body. May be sync or async. */
  build: () => string | Promise<string>;
  /** Toast text after a successful download. Defaults to "CSV downloaded". */
  downloadSuccessMessage?: string;
  /** Toast text after a successful clipboard copy. */
  copySuccessMessage?: string;
  /** Toast text on failure. */
  errorMessage?: string;
  /** Suppress the "Copy to Clipboard" entry. */
  excludeCopy?: boolean;
  /** Override labels (e.g. "Download by-page CSV"). */
  downloadLabel?: string;
  copyLabel?: string;
  /** Add `separatorBefore: true` to the first item, useful for multi-CSV menus. */
  separatorBeforeFirst?: boolean;
}

/**
 * Returns the standard pair of Export menu items for a single CSV.
 *
 * Wraps `downloadCsv` / `copyCsv` with sonner toasts so call sites don't
 * have to repeat the same try/catch boilerplate.
 */
export function csvExportItems({
  filename,
  build,
  downloadSuccessMessage = "CSV downloaded",
  copySuccessMessage = "Copied to clipboard",
  errorMessage = "Export failed",
  excludeCopy = false,
  downloadLabel = "Download CSV",
  copyLabel = "Copy to Clipboard",
  separatorBeforeFirst = false,
}: CsvExportConfig): ExportMenuItem[] {
  const items: ExportMenuItem[] = [
    {
      key: `download:${filename}`,
      label: downloadLabel,
      icon: Download,
      separatorBefore: separatorBeforeFirst,
      onSelect: async () => {
        try {
          const csv = await build();
          downloadCsv(csv, filename);
          toast.success(downloadSuccessMessage);
        } catch (err) {
          console.error("CSV download failed", err);
          toast.error(errorMessage);
        }
      },
    },
  ];

  if (!excludeCopy) {
    items.push({
      key: `copy:${filename}`,
      label: copyLabel,
      icon: ClipboardCopy,
      onSelect: async () => {
        try {
          const csv = await build();
          await copyCsv(csv);
          toast.success(copySuccessMessage);
        } catch (err) {
          console.error("CSV copy failed", err);
          toast.error(errorMessage);
        }
      },
    });
  }

  return items;
}

export interface TextExportConfig {
  /** File name written to the user's disk on download. */
  filename: string;
  /** Returns the text body. May be sync or async. */
  build: () => string | Promise<string>;
  /** Defaults to `text/plain;charset=utf-8;`. */
  mimeType?: string;
  /** Label rendered in the menu. Defaults to "Download". */
  label?: string;
  icon?: LucideIcon;
  /** Inserts a separator above this item. */
  separatorBefore?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Returns a single Export menu item for a downloadable text payload (e.g.
 * a Markdown evidence pack, a JSON dump). Pair with `csvExportItems` when a
 * single Export menu mixes CSVs with other formats.
 */
export function textExportItem({
  filename,
  build,
  mimeType,
  label = "Download",
  icon = Download,
  separatorBefore = false,
  successMessage = "File downloaded",
  errorMessage = "Export failed",
}: TextExportConfig): ExportMenuItem {
  return {
    key: `download-text:${filename}`,
    label,
    icon,
    separatorBefore,
    onSelect: async () => {
      try {
        const text = await build();
        downloadText(text, filename, mimeType);
        toast.success(successMessage);
      } catch (err) {
        console.error("Text download failed", err);
        toast.error(errorMessage);
      }
    },
  };
}
